import * as z from "zod";

declare const __REPLO_DEV_ALIAS__: string | null | undefined;

// NOTE (Ryan, 2026-05-01): Local miniapps can be opened directly through Caddy
// (outside the editor iframe), so the editor does not get a chance to append
// `agentRouting` to the URL. The Vite plugin bakes the current worktree's
// REPLO_DEV_ALIAS into the bundle so direct visits still route to the same
// coordinator DO that the editor would use.
export function getLocalDevAgentRouting(): string {
  // eslint-disable-next-line replo/no-as-cast
  const browserGlobal = globalThis as {
    __REPLO_DEV_ALIAS__?: string | null;
    location?: Location;
  };
  if (browserGlobal.location?.hostname !== "localhost") {
    return "";
  }
  const devAlias =
    typeof __REPLO_DEV_ALIAS__ === "undefined"
      ? (browserGlobal.__REPLO_DEV_ALIAS__ ?? null)
      : __REPLO_DEV_ALIAS__;
  if (!devAlias) {
    return "";
  }
  return JSON.stringify({
    devAlias,
    initialSnapshotRef: null,
  });
}

export function getAgentRoutingParams(): Record<string, string> {
  // getHostParam falls back to the boot-captured params: the in-app router
  // strips bootstrap query params after the first navigation, and losing
  // `agentRouting` here would silently re-route to a *different* coordinator
  // DO (the local-dev fallback has no initialSnapshotRef), which then boots a
  // second sandbox from the wrong snapshot.
  const agentRouting =
    getHostParam("agentRouting") ?? getLocalDevAgentRouting();
  return agentRouting ? { agentRouting } : {};
}

const routingFieldsSchema = z.object({
  devAlias: z.string().nullish(),
  initialSnapshotRef: z.string().nullish(),
});

// NOTE (Max, 2026-06-24): Parse the serialized `agentRouting` JSON into
// top-level fields. Shared by the miniapp DO-name builder and the editor's
// RealtimeConnection so both bake the same routing into the coordinator DO name.
export function parseAgentRoutingFields(params: Record<string, string>): {
  devAlias: string | null;
  initialSnapshotRef: string | null;
} {
  const raw = params.agentRouting;
  if (!raw) {
    return { devAlias: null, initialSnapshotRef: null };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { devAlias: null, initialSnapshotRef: null };
  }
  const result = routingFieldsSchema.safeParse(parsed);
  if (!result.success) {
    return { devAlias: null, initialSnapshotRef: null };
  }
  return {
    devAlias: result.data.devAlias ?? null,
    initialSnapshotRef: result.data.initialSnapshotRef ?? null,
  };
}

export function getInitialMiniappRoute(): string | null {
  return new URLSearchParams(window.location.search).get("miniappRoute");
}

// Captured when this module first loads (iframe boot) — before the in-app router
// navigates and strips the bootstrap query params (assetOrigin, projectId, ...)
// the host appended to the iframe URL. Reading them lazily from the live
// location would miss them after the first navigation.
const initialSearchParams = new URLSearchParams(
  globalThis.location?.search ?? "",
);

/**
 * Read a search param the host appended to the iframe URL (e.g. a `preview`
 * widget's page). Falls back to the boot-captured params so it survives the
 * router stripping bootstrap params after the first navigation.
 */
export function getHostParam(name: string): string | null {
  const current = new URLSearchParams(globalThis.location?.search ?? "").get(
    name,
  );
  return current ?? initialSearchParams.get(name);
}

function getReferrerOrigin(): string | null {
  const referrer = typeof document === "undefined" ? null : document.referrer;
  if (!referrer) {
    return null;
  }
  try {
    return new URL(referrer).origin;
  } catch {
    return null;
  }
}

export function getEditorAssetOrigin(): string | null {
  return (
    getHostParam("assetOrigin") ??
    getHostParam("parentOrigin") ??
    getReferrerOrigin()
  );
}

/**
 * Resolve an editor-served asset path (e.g. `/images/app-icons/tasks.png`) to an
 * absolute URL on the editor origin. Widget iframes normally receive this as
 * `assetOrigin`; referrer covers embedded surfaces that boot without it.
 */
export function getEditorAssetUrl(path: string | null): string | null {
  if (!path || /^https?:\/\//.test(path) || !path.startsWith("/")) {
    return path;
  }
  const assetOrigin = getEditorAssetOrigin();
  return assetOrigin ? `${assetOrigin}${path}` : path;
}

/**
 * Resolve an editor route (e.g. `/agent/:projectId/home`) to an absolute URL on
 * the editor origin. Mini-apps are served from their own origin, so a top-level
 * navigation handed to the host via `openExternalUrl` has to be anchored to the
 * editor rather than to `window.location`.
 */
export function getEditorUrl(path: string): string {
  return new URL(path, getEditorAssetOrigin() ?? globalThis.location.origin)
    .href;
}

export function getMiniappAssetUrl(path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const miniappBasePath =
    globalThis.location?.pathname.match(/^\/miniapp\/[^/]+/)?.[0] ?? "";
  return `${miniappBasePath}${normalizedPath}`;
}

export function isWidgetLibraryPreview(): boolean {
  return getHostParam("widgetPreviewMode") === "library";
}
